import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar, Menu, MenuItem,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Chip, Select, FormControl, InputLabel, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowBack, ExitToApp, LocalHospital, CheckCircle, Cancel,
  Timelapse, BeachAccess, Today, Save, GroupAdd,
} from '@mui/icons-material';
import { authService, userService, attendanceService } from '../../services/api';

const STATUSES = ['Present', 'Absent', 'HalfDay', 'WeeklyOff', 'Holiday'];

const STATUS_COLOR = {
  Present:   'success',
  Absent:    'error',
  HalfDay:   'warning',
  WeeklyOff: 'default',
  Holiday:   'info',
};

const STATUS_ICON = {
  Present:   <CheckCircle fontSize="small" />,
  Absent:    <Cancel fontSize="small" />,
  HalfDay:   <Timelapse fontSize="small" />,
  WeeklyOff: <BeachAccess fontSize="small" />,
  Holiday:   <Today fontSize="small" />,
};

const todayIso = () => new Date().toISOString().split('T')[0];

const monthName = (m) =>
  new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' });

export default function AttendancePage() {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [anchorEl, setAnchorEl] = useState(null);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year,  setYear]  = useState(now.getFullYear());
  const [selectedDate, setSelectedDate] = useState(todayIso());

  const [staff,      setStaff]      = useState([]);
  const [attendance, setAttendance] = useState([]);   // [{userId, date, status, notes}]
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);

  // local edits before saving: { "userId_date": status }
  const [edits, setEdits] = useState({});

  // Bulk-mark dialog
  const [bulkOpen,   setBulkOpen]   = useState(false);
  const [bulkStatus, setBulkStatus] = useState('Present');
  const [bulkSaving, setBulkSaving] = useState(false);

  // View mode: 'day' = single date column | 'month' = calendar summary
  const [viewMode, setViewMode] = useState('day');

  const loadStaff = useCallback(async () => {
    try {
      const res = await userService.getAllUsers();
      setStaff(res.data.filter(u => u.role !== 'Admin'));
    } catch { /* ignore */ }
  }, []);

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await attendanceService.getAll({ month, year });
      setAttendance(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { loadStaff(); }, [loadStaff]);
  useEffect(() => { loadAttendance(); setEdits({}); }, [loadAttendance]);

  // Build lookup: "userId_dateStr" => status
  const attMap = {};
  attendance.forEach(a => {
    const d = a.date.split('T')[0];
    attMap[`${a.userId}_${d}`] = a.status;
  });

  const getStatus = (userId, date) => {
    const key = `${userId}_${date}`;
    return edits[key] ?? attMap[key] ?? null;
  };

  const handleEdit = (userId, date, status) => {
    const key = `${userId}_${date}`;
    setEdits(prev => ({ ...prev, [key]: status }));
  };

  const handleSaveDay = async () => {
    const entries = Object.entries(edits);
    if (!entries.length) return;
    setSaving(true);
    try {
      for (const [key, status] of entries) {
        const [uid, date] = key.split('_');
        await attendanceService.upsert({ userId: parseInt(uid), date, status });
      }
      setEdits({});
      await loadAttendance();
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const handleBulkMark = async () => {
    setBulkSaving(true);
    try {
      await attendanceService.bulkMark({ date: selectedDate, status: bulkStatus });
      setBulkOpen(false);
      await loadAttendance();
      setEdits({});
    } catch { /* ignore */ }
    finally { setBulkSaving(false); }
  };

  // Days in current month for month view
  const daysInMonth = new Date(year, month, 0).getDate();
  const daysList = Array.from({ length: daysInMonth }, (_, i) => {
    const d = String(i + 1).padStart(2, '0');
    const m = String(month).padStart(2, '0');
    return `${year}-${m}-${d}`;
  });

  const pendingEdits = Object.keys(edits).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <AppBar position="static" className="medical-gradient">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <LocalHospital sx={{ fontSize: 36, mr: 1.5 }} />
          <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Staff Attendance
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
            <IconButton size="large" onClick={e => setAnchorEl(e.currentTarget)} color="inherit">
              <Avatar sx={{ bgcolor: '#1565c0' }}>{user?.firstName?.charAt(0)}</Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem onClick={() => { authService.logout(); navigate('/'); }}>
                <ExitToApp sx={{ mr: 1 }} /> Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        {/* Controls */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
            >
              <ToggleButton value="day">Day View</ToggleButton>
              <ToggleButton value="month">Month View</ToggleButton>
            </ToggleButtonGroup>

            {viewMode === 'day' && (
              <TextField
                label="Date"
                type="date"
                size="small"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 170 }}
              />
            )}

            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Month</InputLabel>
              <Select value={month} label="Month" onChange={e => setMonth(e.target.value)}>
                {Array.from({ length: 12 }, (_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>{monthName(i + 1)}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={e => setYear(e.target.value)}>
                {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              {viewMode === 'day' && (
                <Button
                  variant="outlined"
                  startIcon={<GroupAdd />}
                  onClick={() => setBulkOpen(true)}
                  size="small"
                >
                  Bulk Mark
                </Button>
              )}
              {pendingEdits > 0 && (
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                  onClick={handleSaveDay}
                  disabled={saving}
                  size="small"
                  color="success"
                >
                  Save {pendingEdits} Change{pendingEdits > 1 ? 's' : ''}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Status legend */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {STATUSES.map(s => (
            <Chip key={s} icon={STATUS_ICON[s]} label={s} color={STATUS_COLOR[s]} size="small" variant="outlined" />
          ))}
        </Box>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : viewMode === 'day' ? (
          /* ── Day View ── */
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: '#1565c0' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Staff Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Role</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>
                      Status — {new Date(selectedDate).toLocaleDateString('en-IN')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staff.map((s, idx) => {
                    const status = getStatus(s.id, selectedDate);
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{s.firstName} {s.lastName}</TableCell>
                        <TableCell>
                          <Chip label={s.role} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 140 }}>
                            <Select
                              value={status || ''}
                              displayEmpty
                              onChange={e => handleEdit(s.id, selectedDate, e.target.value)}
                              renderValue={v => v
                                ? <Chip icon={STATUS_ICON[v]} label={v} color={STATUS_COLOR[v]} size="small" />
                                : <Typography color="text.secondary" variant="body2">— not marked —</Typography>
                              }
                            >
                              {STATUSES.map(st => (
                                <MenuItem key={st} value={st}>
                                  <Chip icon={STATUS_ICON[st]} label={st} color={STATUS_COLOR[st]} size="small" />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {staff.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No staff found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          /* ── Month View — summary table ── */
          <Paper elevation={3} sx={{ borderRadius: 2, overflowX: 'auto' }}>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead sx={{ bgcolor: '#1565c0' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, minWidth: 160, position: 'sticky', left: 0, bgcolor: '#1565c0', zIndex: 1 }}>
                      Staff
                    </TableCell>
                    {daysList.map(d => (
                      <TableCell key={d} align="center" sx={{ color: '#fff', fontWeight: 700, minWidth: 36, p: '4px 2px' }}>
                        {parseInt(d.split('-')[2])}
                      </TableCell>
                    ))}
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">P</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">A</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">H</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {staff.map(s => {
                    let p = 0, a = 0, h = 0;
                    return (
                      <TableRow key={s.id} hover>
                        <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}>
                          {s.firstName} {s.lastName}
                        </TableCell>
                        {daysList.map(d => {
                          const st = attMap[`${s.id}_${d}`];
                          if (st === 'Present') p++;
                          if (st === 'Absent')  a++;
                          if (st === 'HalfDay') h++;
                          return (
                            <TableCell key={d} align="center" sx={{ p: '2px' }}>
                              {st ? (
                                <Tooltip title={st}>
                                  <span>
                                    <Chip
                                      label={st[0]}
                                      color={STATUS_COLOR[st]}
                                      size="small"
                                      sx={{ minWidth: 24, height: 24, fontSize: '0.7rem', cursor: 'default' }}
                                    />
                                  </span>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.disabled">–</Typography>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell align="center" sx={{ fontWeight: 700, color: 'success.main' }}>{p}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: 'error.main' }}>{a}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: 'warning.main' }}>{h}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      {/* Bulk Mark Dialog */}
      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)}>
        <DialogTitle>Bulk Mark All Staff</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Mark all non-admin staff as:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={bulkStatus} label="Status" onChange={e => setBulkStatus(e.target.value)}>
              {STATUSES.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Date: {new Date(selectedDate).toLocaleDateString('en-IN')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkMark}
            disabled={bulkSaving}
            startIcon={bulkSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Mark All
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
